import {useEffect, useReducer} from 'react'
import type {SignDef, WizardStepDef} from '../../lib/sign-catalog'
import {stepTitle} from '../../lib/sign-catalog'
import {wizardReducer, type WizardState} from './reducer'
import {ContentStep} from './steps/ContentStep'
import {SizeStep} from './steps/SizeStep'
import {BackgroundStep} from './steps/BackgroundStep'
import {ArrowStep} from './steps/ArrowStep'
import {PreviewStep} from './steps/PreviewStep'
import {ExportStep} from './steps/ExportStep'

export interface SignWizardProps {
    readonly def: SignDef
}

function initialState(def: SignDef): WizardState {
    return {stepIndex: 0, stepCount: def.steps.length, input: def.defaults}
}

export function SignWizard({def}: SignWizardProps) {
    const [state, dispatch] = useReducer(wizardReducer, def, initialState)

    useEffect(() => {
        dispatch({type: 'reset', input: def.defaults, stepCount: def.steps.length})
    }, [def])

    const step = def.steps[state.stepIndex]
    const isLast = state.stepIndex === def.steps.length - 1

    return (
        <div className='space-y-8'>
            <ProgressRail
                steps={def.steps}
                currentIndex={state.stepIndex}
            />

            <StepBody
                step={step}
                def={def}
                input={state.input}
                onChange={patch => dispatch({type: 'patch', patch})}
            />

            <footer className='flex items-center gap-4 pt-4 border-t border-foreground/10'>
                <button
                    type='button'
                    onClick={() => dispatch({type: 'prev'})}
                    disabled={state.stepIndex === 0}
                    className='font-mono text-xs tracking-[0.2em] uppercase px-4 py-2 text-foreground/70 hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed'
                >
                    ← Atpakaļ
                </button>
                <div className='flex-1' />
                {!isLast && (
                    <button
                        type='button'
                        onClick={() => dispatch({type: 'next'})}
                        className='font-mono text-xs tracking-[0.2em] uppercase px-4 py-2 text-primary hover:underline'
                    >
                        Tālāk →
                    </button>
                )}
            </footer>
        </div>
    )
}

interface ProgressRailProps {
    readonly steps: readonly WizardStepDef[]
    readonly currentIndex: number
}

function ProgressRail({steps, currentIndex}: ProgressRailProps) {
    return (
        <ol className='flex items-center gap-2 flex-wrap'>
            {steps.map((s, i) => {
                const active = i === currentIndex
                const done = i < currentIndex
                return (
                    <li
                        key={i}
                        className={
                            'font-mono text-[10px] tracking-[0.25em] uppercase px-3 py-1.5 '
                            + (active ? 'bg-surface-container-high text-foreground'
                            : done ? 'text-foreground/60'
                            : 'text-foreground/30')
                        }
                    >
                        {`${String(i + 1).padStart(2, '0')} · ${stepTitle(s)}`}
                    </li>
                )
            })}
        </ol>
    )
}

interface StepBodyProps {
    readonly step: WizardStepDef
    readonly def: SignDef
    readonly input: WizardState['input']
    readonly onChange: (patch: Partial<WizardState['input']>) => void
}

function StepBody({step, def, input, onChange}: StepBodyProps) {
    switch (step.kind) {
        case 'content':
            return (
                <ContentStep
                    input={input}
                    onChange={onChange}
                />
            )
        case 'size':
            return (
                <SizeStep
                    def={def}
                    input={input}
                    onChange={onChange}
                />
            )
        case 'background':
            return (
                <BackgroundStep
                    input={input}
                    onChange={onChange}
                    allowBgColors={step.allowBgColors}
                    allowStrike={step.allowStrike}
                />
            )
        case 'arrow':
            return (
                <ArrowStep
                    input={input}
                    onChange={onChange}
                />
            )
        case 'preview':
            return (
                <PreviewStep
                    def={def}
                    input={input}
                />
            )
        case 'export':
            return (
                <ExportStep
                    def={def}
                    input={input}
                />
            )
        case 'custom': {
            const Component = step.Component
            return (
                <Component
                    input={input}
                    onChange={onChange}
                />
            )
        }
    }
}
